import { Action, Macro, PressureMacro } from '../types/macro'
import type { Capability, MacroCapability } from '../types/manifest'

/** 单条宏编码后的默认最大字节数（键盘标准）。 */
export const DEFAULT_MACRO_MAX_LENGTH = 512

/**
 * 从 manifest capabilities 读取单条宏最大字节数。
 * 未声明或非法时使用 fallback（默认 512）。
 */
export function getMacroMaxLength(
  capabilities?: Capability[] | null,
  fallback: number = DEFAULT_MACRO_MAX_LENGTH,
): number {
  const cap = capabilities?.find((item): item is MacroCapability => item.type === 'macro')
  const maxLength = cap?.config.maxLength
  if (typeof maxLength === 'number' && Number.isFinite(maxLength) && maxLength > 0) {
    return maxLength
  }
  return fallback
}

/**
 * 从宏动作数据字节数组中解析出 Action 列表，并判断“忽略延时”标志。
 * 数据格式与 CH585M 固件协议一致：
 *   - 前 4 字节为保留/长度信息，实际动作数据从第 4 字节开始解析
 *   - 每个动作：2字节状态（bit0=类型，bit1-15=延时） + 1字节键码
 *   - 鼠标移动键(0xF7) 额外占用 3 字节坐标
 * @param actionData 动作数据原始字节数组（通常不包含宏名称）
 * @returns 动作列表与忽略延时标记
 */
export function parseMacroActions(actionData: number[]): {
  actions: Action[]
  ignoreDelay: boolean
} {
  const actionList: Action[] = []

  if (actionData.length >= 4) {
    let parseOffset = 4 // 跳过前 4 字节（宏长度+循环次数占位）
    while (parseOffset + 2 <= actionData.length) {
      // 解析动作状态（小端序）
      const actionStatusLow = actionData[parseOffset]
      const actionStatusHigh = actionData[parseOffset + 1]
      const actionStatus = (actionStatusHigh << 8) | actionStatusLow
      const actionType = actionStatus & 0x0001 // 0=弹起，1=按下
      const delayTime = (actionStatus >> 1) & 0x7fff // 延时(ms)

      if (parseOffset + 2 >= actionData.length) break
      const keyCode = actionData[parseOffset + 2]
      let advance = 3

      const action: Action = {
        time: delayTime,
        type: actionType,
        key: keyCode,
      }

      // 鼠标移动动作 (0xF7) 附加坐标
      if (keyCode === 0xf7 && parseOffset + 5 < actionData.length) {
        const xyByte = actionData[parseOffset + 3]
        const xLow = actionData[parseOffset + 4]
        const yLow = actionData[parseOffset + 5]
        const xHigh = (xyByte >> 4) & 0x0f
        const yHigh = xyByte & 0x0f
        const x = (xHigh << 8) | xLow
        const y = (yHigh << 8) | yLow
        action.x = x > 0x7ff ? x - 0x1000 : x
        action.y = y > 0x7ff ? y - 0x1000 : y
        advance = 6
      }

      // 鼠标按键
      if (keyCode === 0xf8 && parseOffset + 5 < actionData.length) {
        const mouseByte = actionData[parseOffset + 3]
        action.mouseKey = [
          (mouseByte >> 0) & 1, // bit0 左键
          (mouseByte >> 1) & 1, // bit1 右键
          (mouseByte >> 2) & 1, // bit2 中键
          (mouseByte >> 3) & 1, // bit4 按键4
          (mouseByte >> 4) & 1, // bit5 按键5
        ]
        const rawX = actionData[parseOffset + 4] | (actionData[parseOffset + 5] << 8)
        action.x = rawX > 0x7fff ? rawX - 0x10000 : rawX
        const rawY = actionData[parseOffset + 6] | (actionData[parseOffset + 7] << 8)
        action.y = rawY > 0x7fff ? rawY - 0x10000 : rawY
        const rawVWheel = actionData[parseOffset + 8]
        action.vWheel = rawVWheel > 127 ? rawVWheel - 256 : rawVWheel
        const rawHWheel = actionData[parseOffset + 9]
        action.hWheel = rawHWheel > 127 ? rawHWheel - 256 : rawHWheel
        advance = 10
      }

      actionList.push(action)
      parseOffset += advance
    }
  }

  /**
   * 下面是软件端的说法：
   * 这个是因为忽略延时实际上是界面上存在的按钮，但固件是不支持记录这一个字段的，但界面上尽量写入和读取按钮状态是一致的。
   * 所以我们做了统一的逻辑处理，如果界面上用户勾选了忽略延时，当前存在的所有延时统一改为25ms，读取宏时，如果每个延时都是25ms，就默认用户勾选了忽略延时选项。
   * 2026/7/8 测试反馈25ms太慢了，发现软件端开启后是2ms故修改
   */
  const ignoreDelay = actionList.length > 0 && actionList.every((item) => item.time === 2)

  return { actions: actionList, ignoreDelay }
}

/**
 * 将 Macro 对象编码为完整的宏数据字节数组（含长度头部，符合 CH585M 固件格式）。
 * 结构：2字节总长度（小端） + 2字节循环次数（小端） + 动作数据
 * @param macro 宏对象（包含 loop、ignoreDelay、package）
 * @param maxLen 单条宏最大字节数，应由调用方从 manifest `macro.maxLength` 传入
 * @returns 编码后的字节数组（超出 maxLen 时截断）
 */
export function encodeMacroData(
  macro: Macro | PressureMacro,
  maxLen?: number,
): {
  fullMacroBytes: number[]
  macroLength: number
} {
  // 1. 构建动作数据部分
  const bodyBytes: number[] = []

  // 循环次数（2字节，小端）
  const loopCount = macro.loop ?? 0x0000
  bodyBytes.push(loopCount & 0xff)
  bodyBytes.push((loopCount >> 8) & 0xff)

  const actionList = macro.package ?? []

  // 如果loop模式为0xfffe,所有没有延时的动作都需要补齐20ms延时，否则键与键之间/循环衔接处无法正常loop
  if (loopCount === 0xfffe) {
    for (const action of actionList) {
      if (action.time === 0) {
        action.time = 20
      }
    }
  }

  const effectiveMaxLen = maxLen ?? DEFAULT_MACRO_MAX_LENGTH
  // 头部固定开销：2字节 macroLength + bodyBytes 开头2字节循环次数已添加
  const headerSize = 2

  for (const action of actionList) {
    const delayTime = macro.ignoreDelay ? 2 : Math.min(Math.max(action.time, 0), 0x7fff)
    const actionType = action.type === 1 ? 1 : 0
    const keyCode = Math.min(Math.max(action.key, 0), 0xff)

    // 计算当前 action 需要的字节数
    let actionByteSize = 3 // 基础：2字节状态 + 1字节键码
    if (keyCode === 0xf7) {
      actionByteSize = 6 // +3 坐标
    } else if (keyCode === 0xf8) {
      actionByteSize = 10 // +7 鼠标数据
    }

    // 检查添加此 action 后是否超出最大长度
    if (headerSize + bodyBytes.length + actionByteSize > effectiveMaxLen) {
      break
    }

    const actionStatus = (delayTime << 1) | actionType
    bodyBytes.push(actionStatus & 0xff)
    bodyBytes.push((actionStatus >> 8) & 0xff)
    bodyBytes.push(keyCode)

    // 鼠标移动动作附加坐标
    if (keyCode === 0xf7) {
      const to12BitSigned = (val: number) => Math.min(Math.max(val, -2048), 2047) & 0x0fff
      const x12 = to12BitSigned(action.x ?? 0)
      const y12 = to12BitSigned(action.y ?? 0)
      const xHigh = (x12 >> 8) & 0x0f
      const xLow = x12 & 0xff
      const yHigh = (y12 >> 8) & 0x0f
      const yLow = y12 & 0xff
      bodyBytes.push((xHigh << 4) | yHigh)
      bodyBytes.push(xLow)
      bodyBytes.push(yLow)
    }

    if (keyCode === 0xf8) {
      let mouseByte = 0
      if (action.mouseKey) {
        if (action.mouseKey[0]) mouseByte |= 1 << 0
        if (action.mouseKey[1]) mouseByte |= 1 << 1
        if (action.mouseKey[2]) mouseByte |= 1 << 2
        if (action.mouseKey[3]) mouseByte |= 1 << 3
        if (action.mouseKey[4]) mouseByte |= 1 << 4
      }
      bodyBytes.push(mouseByte)

      // X 轴 int16 小端（有符号→无符号补码）
      const rawX = action.x ?? 0
      const xVal = rawX & 0xffff
      bodyBytes.push(xVal & 0xff)
      bodyBytes.push((xVal >> 8) & 0xff)

      // Y 轴 int16 小端
      const rawY = action.y ?? 0
      const yVal = rawY & 0xffff
      bodyBytes.push(yVal & 0xff)
      bodyBytes.push((yVal >> 8) & 0xff)

      // 垂直滚轮 int8（文档：Byte9 垂直滚轮）
      const rawVWheel = action.vWheel ?? 0
      bodyBytes.push(rawVWheel & 0xff)

      // 水平滚轮 int8（文档：Byte10 水平滚轮）
      const rawHWheel = action.hWheel ?? 0
      bodyBytes.push(rawHWheel & 0xff)
    }
  }

  // 2. 计算总长度并插入头部
  // bodyBytes 已包含 2 字节循环次数，所以 totalActionBytes = bodyBytes.length - 2
  const totalActionBytes = bodyBytes.length - 2
  const macroLength = totalActionBytes + 2 + 2 // 总长度含自身的2字节和循环次数2字节
  let fullMacroBytes: number[] = [macroLength & 0xff, (macroLength >> 8) & 0xff, ...bodyBytes]

  // 3. 最终安全截断（兜底保护，正常情况下循环中已控制）
  if (fullMacroBytes.length > effectiveMaxLen) {
    fullMacroBytes = fullMacroBytes.slice(0, effectiveMaxLen)
  }

  return { fullMacroBytes, macroLength }
}
